import { ArrowUp, ArrowDown } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface MetricCardProps {
  title: string;
  description: string;
  /** The headline number. For count cards: the count. For percent cards: the percent. */
  value: number | string | null;
  /** Optional denominator shown as "/N" after the value (e.g. "360 /1,000"). */
  denominator?: number | string | null;
  /** Suffix appended to value when present (e.g. "%"). */
  unit?: string;
  /** The trend number to display; positive = up, negative = down. */
  trend?: number | null;
  /** Human-readable comparison period shown inside the trend tooltip. */
  comparisonLabel?: string;
  isLoading?: boolean;
}

const formatTrend = (n: number) => {
  const abs = Math.abs(n);
  return Number.isInteger(abs) ? `${abs}%` : `${abs.toFixed(1)}%`;
};

export function MetricCard({
  title,
  description,
  value,
  denominator,
  unit,
  trend,
  comparisonLabel,
  isLoading = false,
}: MetricCardProps) {
  const showTrend = !isLoading && trend !== null && trend !== undefined;
  const isUp = (trend ?? 0) >= 0;

  return (
    <div className={cn(
      'rounded-3xl border border-gray-500 p-5 flex flex-col gap-3 bg-white text-left transition-all',
    )}>
      <div className='flex flex-col gap-0.5'>
        <h3 className='text-[13px] leading-[18px] font-medium text-[#2b2e34]'>{title}</h3>
        <p className='text-[12px] leading-[16px] font-normal text-[#5f656f]'>{description}</p>
      </div>
      <div className='flex items-center gap-3 min-h-8 mt-auto'>
        {isLoading ? (
          <>
            <Skeleton className='h-7 w-24 rounded-md' />
            <Skeleton className='h-5 w-14 rounded-full' />
          </>
        ) : (
          <>
            <div className='flex items-center gap-1'>
              <span className='text-[20px] leading-[28px] font-semibold text-[#2b2e34]'>
                {value === null || value === undefined ? '—' : `${value}${unit ?? ''}`}
              </span>
              {denominator !== null && denominator !== undefined ? (
                <span className='text-[13px] leading-[18px] font-normal text-[#5f656f]'>
                  /{typeof denominator === 'number' ? denominator.toLocaleString() : denominator}
                </span>
              ) : null}
            </div>
            {showTrend ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      className={cn(
                        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-2xl text-[12px] leading-[16px] font-normal cursor-help',
                        isUp ? 'bg-[#dcfce7] text-[#22c55e]' : 'bg-[#fee2e2] text-[#ef4444]',
                      )}
                    >
                      {isUp ? <ArrowUp className='h-3 w-3' /> : <ArrowDown className='h-3 w-3' />}
                      {formatTrend(trend ?? 0)}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className='text-xs'>
                      {comparisonLabel ? `vs ${comparisonLabel}` : 'vs previous period'}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
